import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

/**
 * Internal toast model. SA5 public spec:
 *   { id, variant, title, description?, duration? }
 * In the store we keep `message` as an alias for `description` to remain
 * backward compatible with earlier callers that passed a plain string.
 */
export interface ToastItem {
  id: string
  variant: ToastVariant
  title?: string
  /** Alias for description; surfaced via <ToastContainer /> as description. */
  message: string
  duration: number
  createdAt: number
}

export interface ToastInput {
  variant: ToastVariant
  title?: string
  description?: string
  duration?: number
}

let counter = 0
function nextId(): string {
  counter += 1
  return `t_${Date.now().toString(36)}_${counter.toString(36)}`
}

const DEFAULTS: Record<ToastVariant, number> = {
  success: 3500,
  error:   5500,
  warning: 4500,
  info:    3500,
}

export const useNotificationStore = defineStore('notification', () => {
  const toasts = ref<ToastItem[]>([])

  // v3.14.0 (T1): 堆叠上限 — 通知风暴 (tracker 洪水触发批量下载错误 +
  // webhook 回声) 此前会把 DOM 推到上百节点, UI 冻结。超出上限时最旧的
  // 先被挤掉; 同 variant+title 去重只刷新时间戳 (重复错误不堆叠)。
  const MAX_TOASTS = 6

  function push(item: ToastInput): string {
    const id = nextId()
    const variant = item.variant
    const description = item.description ?? ''
    const duration = item.duration ?? DEFAULTS[variant]
    if (!item.title && !description) {
      // refuse empty toasts
      return id
    }
    // 同 variant+title+message 已存在 → 复用并重置其自动消失计时
    // (通过删除再 push 实现置顶), 避免同一错误刷屏堆叠。
    const dupIdx = toasts.value.findIndex(
      (t) => t.variant === variant && t.title === item.title && t.message === description,
    )
    if (dupIdx >= 0) {
      const existing = toasts.value[dupIdx]
      toasts.value.splice(dupIdx, 1)
      toasts.value.push(existing)
      if (existing.duration > 0 && typeof window !== 'undefined') {
        window.setTimeout(() => dismiss(existing.id), existing.duration)
      }
      return existing.id
    }
    const toast: ToastItem = {
      id,
      variant,
      title: item.title,
      message: description,
      duration,
      createdAt: Date.now(),
    }
    toasts.value.push(toast)
    while (toasts.value.length > MAX_TOASTS) {
      toasts.value.shift() // 最旧的挤出
    }
    if (duration > 0 && typeof window !== 'undefined') {
      window.setTimeout(() => dismiss(id), duration)
    }
    return id
  }

  function dismiss(id: string): void {
    toasts.value = toasts.value.filter((t) => t.id !== id)
  }

  function clear(): void {
    toasts.value = []
  }

  // Convenience helpers — SA5 spec: (title, description?, duration?)
  function success(title: string, description?: string, duration?: number): string {
    return push({ variant: 'success', title, description, duration })
  }
  function error(title: string, description?: string, duration?: number): string {
    return push({ variant: 'error', title, description, duration })
  }
  function warning(title: string, description?: string, duration?: number): string {
    return push({ variant: 'warning', title, description, duration })
  }
  function info(title: string, description?: string, duration?: number): string {
    return push({ variant: 'info', title, description, duration })
  }

  return {
    toasts,
    push,
    dismiss,
    clear,
    success,
    error,
    warning,
    info,
  }
})
